import { useEffect, useState } from 'react';
import { gradesFor } from '@/lib/boardGrades';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost } from '@/lib/api';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SchoolPicker, resolveSchoolName } from '@/components/common/SchoolPicker';
import { SetupStepper } from '@/components/study/SetupStepper';
import { ContributionNotice } from '@/components/study/ContributionNotice';
import { Loader2 } from 'lucide-react';

export const needsAcademicProfile = user => !user?.onboarding_complete || ['board', 'class_level', 'school', 'state', 'city', 'section'].some(k => !user?.[k]);
const selectClass = 'w-full min-w-0 rounded-lg border border-input bg-background p-2.5 text-sm';

export function AcademicProfileDialog({ open, onSaved, onClose, step = 0, totalSteps = 5, onBack }) {
  const { user, setUser } = useAuth();
  const [boards, setBoards] = useState([]);
  const [form, setForm] = useState({});
  const [place, setPlace] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    setForm({ first_name: user?.first_name || user?.name?.split(' ')[0] || '', last_name: user?.last_name || '',
      board: user?.board || '', class_level: String(user?.class_level || ''), section: user?.section || '' });
    setPlace({ state: user?.state || '', city: user?.city || '', school: user?.school || '', school_other: '' });
    apiGet('/onboarding/options').then(d => setBoards(d.boards || [])).catch(e => setError(e.message));
  }, [open, user]);
  const change = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const save = async () => {
    setBusy(true); setError('');
    try {
      const updated = await apiPost('/onboarding/complete', { ...form, ...place, school: resolveSchoolName(place) });
      setUser({ ...user, ...updated });
      onSaved?.(updated);
    } catch (e) { setError(e.message || 'Could not save your school details. Please try again.'); }
    finally { setBusy(false); }
  };
  const valid = form.first_name?.trim() && form.board && form.class_level && form.section && place.state && place.city && resolveSchoolName(place);
  const blocking = needsAcademicProfile(user);
  return <Dialog open={open} onOpenChange={o => { if (!o && !blocking) onClose?.(); }}>
    <DialogContent data-testid="academic-profile-dialog" className="max-w-lg max-h-[90dvh] overflow-y-auto"
      hideClose={blocking}
      onEscapeKeyDown={e => { if (blocking) e.preventDefault(); }}
      onPointerDownOutside={e => e.preventDefault()}>
      {blocking && <SetupStepper step={step} total={totalSteps} />}
      {onBack && <button type="button" data-testid="academic-back-btn" onClick={onBack}
        className="text-[12px] font-heading text-slate-500 hover:text-slate-800 self-start">← Back</button>}
      <DialogTitle data-testid="academic-profile-title">Your school & class</DialogTitle>
      <DialogDescription>Your Mode Of Study is saved. Let’s match your learning to your school.</DialogDescription>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs space-y-1">First name<Input data-testid="academic-first-name" value={form.first_name || ''} onChange={e => change('first_name', e.target.value)} /></label>
        <label className="text-xs space-y-1">Last name<Input data-testid="academic-last-name" value={form.last_name || ''} onChange={e => change('last_name', e.target.value)} /></label>
      </div>
      <label className="text-xs space-y-1">Education Board
        <select data-testid="academic-board" className={selectClass} value={form.board || ''} onChange={e => change('board', e.target.value)}>
          <option value="">Select education board</option>
          {form.board && !boards.some(b => b.name === form.board) && <option value={form.board}>{`${form.board} (current)`}</option>}
          {boards.map(b => <option key={b.board_id} value={b.name}>{b.name}</option>)}
        </select>
      </label>
      {!boards.length && <p data-testid="academic-boards-empty" className="text-xs text-muted-foreground">No new boards are currently open for enrolment.</p>}
      {form.board && !form.board.toLowerCase().includes('cbse') && <ContributionNotice board={form.board} />}
      <SchoolPicker value={place} onChange={setPlace} board={form.board} />
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs space-y-1">Class<select data-testid="academic-class" className={selectClass} value={form.class_level || ''} onChange={e => change('class_level', e.target.value)}>
          <option value="">Select class</option>{gradesFor(boards, form.board).map(g => <option key={g} value={g}>{`Class ${g}`}</option>)}
        </select></label>
        <label className="text-xs space-y-1">Section<select data-testid="academic-section" className={selectClass} value={form.section || ''} onChange={e => change('section', e.target.value)}>
          {form.section && !'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').includes(form.section) && form.section !== 'Not applicable' && <option value={form.section}>{form.section}</option>}
          <option value="">Select section</option>{'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(s => <option key={s}>{s}</option>)}<option value="Not applicable">Not applicable</option>
        </select></label>
      </div>
      {error && <p role="alert" data-testid="academic-profile-error" className="text-sm text-destructive">{error}</p>}
      <Button data-testid="academic-profile-save" disabled={!valid || busy} onClick={save}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save & continue</Button>
    </DialogContent>
  </Dialog>;
}